import {
  currentEventMonth,
  eventMonthKey,
  eventMonthLabel,
  filterEventCards,
  groupEventsByMonth,
  type EventCard,
} from "../shared/catalog";
import { eventClassLabel, eventClassOf } from "../shared/event-state";
import { countryName } from "../shared/geo";
import { namedEvent, resolveEventName } from "../shared/event-names";
import { formatAge, formatFirePercent, formatFrp, formatLookClock } from "./format";

export type EventFilters = { country: string; month: string; q: string };

/** Filters + month sections. All filtering is in-memory after /api/events. */
export function renderEventsList(cards: EventCard[], filters: EventFilters, nowMs: number): string {
  const months = monthOptions(cards, nowMs);
  const shown = filterEventCards(cards, filters);
  // Pills follow month + search so unused countries drop away.
  const pillCountries = [...new Set(
    filterEventCards(cards, { ...filters, country: "" }).map((c) => c.country).filter(Boolean),
  )].sort((a, b) => countryName(a).localeCompare(countryName(b)));
  const groups = groupEventsByMonth(shown);
  return `${filterBarHtml(pillCountries, months, filters)}
    ${
      groups.length === 0
        ? `<p class="events-empty">No events in this filter.</p>`
        : groups.map((g) => sectionHtml(g, nowMs)).join("")
    }<p class="basemap-credit"><a href="https://openmaptiles.org/" target="_blank" rel="noopener">© OpenMapTiles</a> · <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap</a> · <a href="/basemap-licenses.txt" target="_blank" rel="noopener">Map design credits</a></p>`;
}

function monthOptions(cards: EventCard[], nowMs: number): string[] {
  const months = uniqueSorted(cards.map((c) => eventMonthKey(c.last_seen)).filter((k) => k !== "unknown")).reverse();
  const current = currentEventMonth(nowMs);
  if (!months.includes(current)) months.unshift(current);
  return months;
}

function filterBarHtml(countries: string[], months: string[], filters: EventFilters): string {
  return `<div class="events-toolbar">
    <div class="events-filters">
      <label class="events-filter events-search">Search
        <input type="search" id="events-search" data-filter="q" value="${escapeHtml(filters.q)}" placeholder="Name or place" autocomplete="off" spellcheck="false" />
      </label>
      <label class="events-filter">Month
        <select data-filter="month">
          <option value="all"${filters.month === "all" ? " selected" : ""}>All months</option>
          ${months.map((m) => optionHtml(m, eventMonthLabel(m), filters.month)).join("")}
        </select>
      </label>
    </div>
    <div class="events-pills" role="listbox" aria-label="Country">
      ${countryPill("", "All", filters.country)}
      ${countries.map((cc) => countryPill(cc, countryName(cc), filters.country)).join("")}
    </div>
  </div>`;
}

function countryPill(value: string, label: string, selected: string): string {
  const on = (selected || "") === value;
  return `<button type="button" data-country="${escapeHtml(value)}" class="${on ? "on" : ""}" role="option" aria-selected="${on}">${escapeHtml(label)}</button>`;
}

function optionHtml(value: string, label: string, selected: string): string {
  return `<option value="${escapeHtml(value)}"${value === selected ? " selected" : ""}>${escapeHtml(label)}</option>`;
}

function sectionHtml(group: { key: string; label: string; cards: EventCard[] }, nowMs: number): string {
  return `<section class="events-month" data-month="${escapeHtml(group.key)}">
    <h2 class="events-month-title">${escapeHtml(group.label)} <em>${group.cards.length}</em></h2>
    <div class="events-grid">${group.cards.map((c) => eventCardHtml(c, nowMs)).join("")}</div>
  </section>`;
}

function eventCardHtml(card: EventCard, nowMs: number): string {
  const place = resolveEventName(card.id, card.place_name) || countryName(card.country || "") || "Unknown";
  const pinned = Boolean(namedEvent(card.id));
  const cls = eventClassOf(card.event_status);
  const spark = miniSpark(card.series ?? []);
  const last = card.last_observed || card.last_seen;
  const span = eventSpanLabel(card);
  const peak =
    card.peak_frp_mw > card.frp_mw + 1 ? `<span class="event-peak">peak ${escapeHtml(formatFrp(card.peak_frp_mw))}</span>` : "";
  return `<button type="button" class="event-card" data-id="${escapeHtml(card.id)}">
    <div class="event-thumb">
      <canvas data-basemap-lat="${card.lat}" data-basemap-lon="${card.lon}" aria-hidden="true"></canvas>
      <i class="event-pin" style="left:50%;top:50%"></i>
      <span class="${card.live ? "event-live" : "event-archive"}">${card.live ? "On the map" : "Archive"}</span>
    </div>
    <div class="event-body">
      <h3 class="event-place" data-lat="${card.lat}" data-lon="${card.lon}" data-cc="${escapeHtml(card.country)}"${pinned ? ' data-named="1"' : ""}>${escapeHtml(place)}</h3>
      <p class="event-where">${escapeHtml(countryName(card.country || ""))}${span ? ` · ${escapeHtml(span)}` : ""}</p>
      <p class="event-class ${cls}">${escapeHtml(eventClassLabel(cls))}${card.fire_probability != null ? ` · ${escapeHtml(formatFirePercent(card.fire_probability))}` : ""}</p>
      <p class="event-frp">${escapeHtml(formatFrp(card.frp_mw))}${peak} · ${card.detection_count} hotspots</p>
      <p class="event-when">${escapeHtml(formatAge(last, nowMs))} · ${escapeHtml(formatLookClock(last, nowMs))}</p>
    </div>
    ${spark}
  </button>`;
}

/** First look → last look, in hours or days. */
export function eventSpanLabel(card: Pick<EventCard, "first_seen" | "last_seen">): string {
  const a = Date.parse(card.first_seen);
  const b = Date.parse(card.last_seen);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return "";
  const hours = (b - a) / 3600_000;
  if (hours < 20) return `${Math.max(1, Math.round(hours))} h`;
  const days = hours / 24;
  return days < 1.5 ? "1 day" : `${Math.round(days)} days`;
}

/** Full-bleed FRP footer. `none` stretches the path — a fixed viewBox + meet letterboxed the line. */
function miniSpark(points: EventCard["series"]): string {
  if (!points || points.length < 2) return "";
  const w = 220;
  const h = 32;
  const padT = 5;
  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.mw);
  const x0 = xs[0]!;
  const x1 = xs[xs.length - 1]!;
  const y1 = Math.max(...ys, 1);
  const xOf = (t: number) => ((t - x0) / Math.max(x1 - x0, 1)) * w;
  const yOf = (mw: number) => h - (mw / y1) * (h - padT);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${xOf(p.t).toFixed(1)} ${yOf(p.mw).toFixed(1)}`).join(" ");
  const fill = `${line} L${w} ${h} L0 ${h} Z`;
  const peak = points.reduce((best, p) => (p.mw > best.mw ? p : best), points[0]!);
  const peakX = ((xOf(peak.t) / w) * 100).toFixed(1);
  const peakY = ((yOf(peak.mw) / h) * 100).toFixed(1);
  return `<div class="event-spark" aria-hidden="true">
    <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">
      <path class="event-spark-fill" d="${fill}"/>
      <path class="event-spark-line" d="${line}"/>
    </svg>
    <i class="event-spark-peak" style="left:${peakX}%;top:${peakY}%"></i>
  </div>`;
}

export function fillEventPlaceNames(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(".event-place").forEach((el) => {
    if (el.dataset.named === "1") return;
    const lat = Number(el.dataset.lat);
    const lon = Number(el.dataset.lon);
    const cc = el.dataset.cc ?? "";
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    void fetch(`/api/place?lat=${lat}&lon=${lon}&cc=${encodeURIComponent(cc)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { name?: string } | null) => {
        if (d?.name) {
          el.textContent = d.name;
          el.dataset.named = "1";
        }
      })
      .catch(() => {
        // Country label is enough if Nominatim is quiet.
      });
  });
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

function escapeHtml(s: string | null | undefined): string {
  return String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}
